import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatures } from "@/contexts/FeatureContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Truck, Store, CreditCard } from "lucide-react";

export default function Checkout() {
  const { items, subtotal, clear, hasRestaurant } = useCart();
  const { user } = useAuth();
  const { isOn } = useFeatures();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    customer_email: user?.email || "",
    customer_phone: user?.phone || "",
    address: "",
    fulfillment: "pickup",
    payment_method: "pay_at_store",
    notes: "",
    promo_code: "",
    scheduled_for: "",
  });
  const [promo, setPromo] = useState(null);

  useEffect(() => { if (items.length === 0) navigate("/"); }, [items, navigate]);

  const tax = (hasRestaurant ? subtotal * 0.0975 : 0);
  let discount = 0;
  if (promo) {
    if (promo.discount_type === "percent") discount = subtotal * (promo.discount_value / 100);
    else if (promo.discount_type === "fixed") discount = promo.discount_value;
  }
  const total = Math.max(0, subtotal - discount + tax);

  const applyPromo = async () => {
    try {
      const { data } = await api.post("/promotions/validate", { code: form.promo_code });
      setPromo(data);
      toast.success(`${data.name} applied`);
    } catch (_) { toast.error("Invalid code"); setPromo(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/orders", { ...form, items });
      clear();
      toast.success(`Order ${data.order_number} placed!`);
      navigate(`/order/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Order failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 grid lg:grid-cols-3 gap-8" data-testid="checkout-page">
      <form onSubmit={submit} className="lg:col-span-2 space-y-6">
        <h1 className="font-heading text-4xl text-[#1A2421]">Checkout</h1>

        <div className="mart-card p-6 space-y-3">
          <h3 className="font-heading text-xl text-[#1A2421]">Contact</h3>
          <input required placeholder="Full name" data-testid="checkout-name" className="w-full border border-[#8A9A86]/40 rounded px-4 py-3" value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})}/>
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Phone" data-testid="checkout-phone" className="border border-[#8A9A86]/40 rounded px-4 py-3" value={form.customer_phone} onChange={(e) => setForm({...form, customer_phone: e.target.value})}/>
            <input type="email" placeholder="Email" data-testid="checkout-email" className="border border-[#8A9A86]/40 rounded px-4 py-3" value={form.customer_email} onChange={(e) => setForm({...form, customer_email: e.target.value})}/>
          </div>
        </div>

        <div className="mart-card p-6 space-y-3">
          <h3 className="font-heading text-xl text-[#1A2421]">Fulfillment</h3>
          <div className="grid grid-cols-2 gap-3">
            <label className={`border rounded-lg p-4 cursor-pointer flex gap-3 items-center ${form.fulfillment === 'pickup' ? 'border-[#2C4A3B] bg-[#F2EFEB]' : 'border-[#8A9A86]/40'}`}>
              <input type="radio" name="ful" value="pickup" checked={form.fulfillment === 'pickup'} onChange={(e) => setForm({...form, fulfillment: e.target.value})} data-testid="ful-pickup" className="accent-[#2C4A3B]"/>
              <Store className="w-5 h-5 text-[#2C4A3B]"/><span>Pickup</span>
            </label>
            {isOn("delivery") && (
              <label className={`border rounded-lg p-4 cursor-pointer flex gap-3 items-center ${form.fulfillment === 'delivery' ? 'border-[#2C4A3B] bg-[#F2EFEB]' : 'border-[#8A9A86]/40'}`}>
                <input type="radio" name="ful" value="delivery" checked={form.fulfillment === 'delivery'} onChange={(e) => setForm({...form, fulfillment: e.target.value})} data-testid="ful-delivery" className="accent-[#2C4A3B]"/>
                <Truck className="w-5 h-5 text-[#2C4A3B]"/><span>Delivery</span>
              </label>
            )}
          </div>
          {form.fulfillment === 'delivery' && (
            <input required placeholder="Delivery address" data-testid="checkout-address" className="w-full border border-[#8A9A86]/40 rounded px-4 py-3" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})}/>
          )}
          <input type="datetime-local" placeholder="Scheduled for" data-testid="checkout-schedule" className="w-full border border-[#8A9A86]/40 rounded px-4 py-3" value={form.scheduled_for} onChange={(e) => setForm({...form, scheduled_for: e.target.value})}/>
        </div>

        <div className="mart-card p-6 space-y-3">
          <h3 className="font-heading text-xl text-[#1A2421] flex items-center gap-2"><CreditCard className="w-5 h-5"/> Payment</h3>
          {[
            { v: "pay_at_store", label: "Pay at Store" },
            { v: "cash_on_pickup", label: "Cash on Pickup" },
            ...(form.fulfillment === 'delivery' ? [{ v: "cash_on_delivery", label: "Cash on Delivery" }] : []),
            { v: "manual_card", label: "Card at terminal (in-store)" },
          ].map((opt) => (
            <label key={opt.v} className={`block border rounded-lg p-3 cursor-pointer ${form.payment_method === opt.v ? 'border-[#2C4A3B] bg-[#F2EFEB]' : 'border-[#8A9A86]/40'}`}>
              <input type="radio" name="pay" value={opt.v} checked={form.payment_method === opt.v} onChange={(e) => setForm({...form, payment_method: e.target.value})} data-testid={`pay-${opt.v}`} className="mr-3 accent-[#2C4A3B]"/>
              {opt.label}
            </label>
          ))}
          <div className="text-xs text-[#5C6B64] flex items-center gap-1.5 mt-2"><ShieldCheck className="w-3.5 h-3.5"/> Online card payments (Stripe, Square, Clover) — coming soon</div>
        </div>

        <textarea rows={3} placeholder="Order notes (allergies, special instructions...)" data-testid="checkout-notes" className="w-full mart-card p-4 border-[#8A9A86]/40" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
      </form>

      <aside className="lg:sticky lg:top-24 h-fit mart-card p-6 space-y-3" data-testid="checkout-summary">
        <h3 className="font-heading text-2xl text-[#1A2421]">Summary</h3>
        <div className="space-y-1 text-sm">
          {items.map((it, idx) => (
            <div key={idx} className="flex justify-between text-[#5C6B64]">
              <span>{it.name} × {it.quantity}</span>
              <span>${(it.unit_price * it.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <hr className="border-[#8A9A86]/30"/>
        <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
        {discount > 0 && <div className="flex justify-between text-green-700"><span>Discount</span><span>-${discount.toFixed(2)}</span></div>}
        {tax > 0 && <div className="flex justify-between text-[#5C6B64]"><span>CA Tax (9.75%)</span><span>${tax.toFixed(2)}</span></div>}
        <div className="flex justify-between font-heading text-2xl text-[#2C4A3B] border-t pt-3 border-[#8A9A86]/30">
          <span>Total</span><span data-testid="checkout-total">${total.toFixed(2)}</span>
        </div>

        <div className="flex gap-2 pt-3">
          <input placeholder="Promo code" data-testid="promo-input" className="flex-1 border border-[#8A9A86]/40 rounded px-3 py-2" value={form.promo_code} onChange={(e) => setForm({...form, promo_code: e.target.value})}/>
          <button type="button" data-testid="promo-apply" onClick={applyPromo} className="border border-[#2C4A3B] text-[#2C4A3B] px-4 py-2 rounded font-semibold text-sm">Apply</button>
        </div>

        <button onClick={submit} data-testid="checkout-submit" type="button" className="btn-mart w-full mt-3 py-4 text-base">Place Order</button>
      </aside>
    </div>
  );
}
